import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { superAdminApi } from '../../services/api';
import toast from 'react-hot-toast';

const PLAN_COLORS = { basic: { bg: '#dbeafe', text: '#1e40af' }, pro: { bg: '#fef9c3', text: '#854d0e' }, enterprise: { bg: '#dcfce7', text: '#166534' } };

const emptyForm = { name: '', slug: '', email: '', phone: '', address: '', plan: 'basic', admin_name: '', admin_email: '', admin_password: '' };

const SuperAdminRestaurants = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showModal, setShowModal]     = useState(false);
  const [form, setForm]               = useState(emptyForm);
  const [saving, setSaving]           = useState(false);
  const [search, setSearch]           = useState('');
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    superAdminApi.getRestaurants()
      .then(res => setRestaurants(res.data.data))
      .catch(() => toast.error('Failed to load restaurants'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await superAdminApi.createRestaurant(form);
      const { restaurant, admin_email, login_url } = res.data.data;
      toast.success(`✅ "${restaurant.name}" created!`);
      setShowModal(false);
      setForm(emptyForm);
      load();
      // Show credentials
      setTimeout(() => {
        toast.success(
          `Admin: ${admin_email}\nLogin: ${window.location.origin}${login_url}`,
          { duration: 8000 }
        );
      }, 500);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create restaurant');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (r) => {
    try {
      await superAdminApi.updateRestaurant(r.id, { is_active: !r.is_active });
      toast.success(`Restaurant ${!r.is_active ? 'activated' : 'disabled'}`);
      load();
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete "${r.name}" and ALL its data? This cannot be undone.`)) return;
    try {
      await superAdminApi.deleteRestaurant(r.id);
      toast.success('Restaurant deleted');
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const filtered = restaurants.filter(r =>
    r.name.toLowerCase().includes(search.toLowerCase()) ||
    r.slug.toLowerCase().includes(search.toLowerCase())
  );

  const inp = (label, name, type = 'text', placeholder = '', required = false) => (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
        {label}{required && ' *'}
      </label>
      <input
        type={type}
        value={form[name]}
        onChange={e => {
          const val = name === 'slug' ? e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') : e.target.value;
          setForm(p => ({ ...p, [name]: val }));
        }}
        required={required}
        placeholder={placeholder}
        style={{
          width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb',
          borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box',
        }}
        onFocus={e => e.target.style.borderColor = '#7c3aed'}
        onBlur={e => e.target.style.borderColor = '#e5e7eb'}
      />
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1f2937', margin: 0 }}>Restaurants</h1>
          <p style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>{restaurants.length} restaurants on platform</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          style={{
            padding: '10px 20px', background: 'linear-gradient(135deg,#7c3aed,#5b21b6)',
            color: '#fff', border: 'none', borderRadius: 10, fontSize: 14,
            fontWeight: 600, cursor: 'pointer',
          }}
        >
          + Add Restaurant
        </button>
      </div>

      {/* Search */}
      <input
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search by name or slug..."
        style={{
          width: '100%', maxWidth: 360, padding: '10px 14px', border: '1.5px solid #e5e7eb',
          borderRadius: 10, fontSize: 14, marginBottom: 20, boxSizing: 'border-box', outline: 'none',
        }}
      />

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', paddingTop: 60, color: '#6b7280' }}>Loading...</div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #ede9fe', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f5f3ff' }}>
                {['#', 'Restaurant', 'Slug', 'Plan', 'Orders', 'Revenue', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6d28d9', textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, index) => (
                <tr key={r.id} style={{ borderTop: '1px solid #f3f4f6' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#faf5ff'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding: '14px 16px', color: '#6b7280', fontWeight: 500, fontSize: 13 }}>{index + 1}</td>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ fontWeight: 600, color: '#1f2937' }}>{r.name}</div>
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>{r.email}</div>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <code style={{ background: '#f5f3ff', color: '#7c3aed', padding: '2px 7px', borderRadius: 5, fontSize: 13 }}>/{r.slug}</code>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ ...PLAN_COLORS[r.plan], padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600 }}>
                      {r.plan}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px', color: '#374151' }}>{r.total_orders || 0}</td>
                  <td style={{ padding: '14px 16px', fontWeight: 600, color: '#7c3aed' }}>
                    ₹{parseFloat(r.total_revenue || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{
                      padding: '4px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600,
                      background: r.is_active ? '#dcfce7' : '#fee2e2',
                      color: r.is_active ? '#166534' : '#991b1b',
                    }}>
                      {r.is_active ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => navigate(`/super-admin/restaurants/${r.id}`)}
                        style={{ padding: '5px 12px', background: '#f5f3ff', color: '#7c3aed', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>
                        View
                      </button>
                      <button onClick={() => handleToggle(r)}
                        style={{ padding: '5px 12px', background: r.is_active ? '#fef3c7' : '#dcfce7', color: r.is_active ? '#92400e' : '#166534', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>
                        {r.is_active ? 'Disable' : 'Enable'}
                      </button>
                      <button onClick={() => handleDelete(r)}
                        style={{ padding: '5px 12px', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} style={{ padding: '40px 16px', textAlign: 'center', color: '#9ca3af' }}>No restaurants found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560,
            maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <div style={{ padding: '24px 28px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1f2937', margin: 0 }}>Add New Restaurant</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#9ca3af' }}>×</button>
            </div>
            <form onSubmit={handleCreate} style={{ padding: '24px 28px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12 }}>Restaurant Info</div>
              {inp('Restaurant Name', 'name', 'text', 'e.g. Cafe UK', true)}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Slug (URL) *</label>
                <div style={{ display: 'flex', alignItems: 'center', border: '1.5px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                  <span style={{ padding: '10px 12px', background: '#f9fafb', fontSize: 13, color: '#9ca3af', borderRight: '1px solid #e5e7eb' }}>yoursite.com/</span>
                  <input
                    value={form.slug}
                    onChange={e => setForm(p => ({ ...p, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                    required placeholder="cafe-uk"
                    style={{ flex: 1, padding: '10px 12px', border: 'none', fontSize: 14, outline: 'none' }}
                  />
                </div>
              </div>
              {inp('Email', 'email', 'email', 'contact@cafe.com')}
              {inp('Phone', 'phone', 'tel', '+91 98765 43210')}
              {inp('Address', 'address', 'text', 'Full address')}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Plan</label>
                <select value={form.plan} onChange={e => setForm(p => ({ ...p, plan: e.target.value }))}
                  style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none' }}>
                  <option value="basic">Basic</option>
                  <option value="pro">Pro</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>

              <div style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: 0.8, margin: '20px 0 12px', paddingTop: 16, borderTop: '1px solid #f3f4f6' }}>
                Admin Account
              </div>
              {inp('Admin Name', 'admin_name', 'text', 'Restaurant Owner', true)}
              {inp('Admin Email', 'admin_email', 'email', 'admin@cafe.com', true)}
              {inp('Admin Password', 'admin_password', 'password', 'Min 6 characters', true)}

              <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                <button type="button" onClick={() => setShowModal(false)}
                  style={{ flex: 1, padding: '11px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="submit" disabled={saving}
                  style={{ flex: 1, padding: '11px', background: saving ? '#9ca3af' : 'linear-gradient(135deg,#7c3aed,#5b21b6)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}>
                  {saving ? 'Creating...' : 'Create Restaurant'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminRestaurants;
